import React from 'react';
import { MARKETING_COMPANY_LOGOS } from '@/data/marketingCompanyLogos';

export type CompanyLogosAppearance = 'default' | 'darkBar';

export interface CompanyLogosSectionProps {
  /** `darkBar`: always show the compact dark strip (e.g. playbooks list). */
  appearance?: CompanyLogosAppearance;
}

const CompanyLogosSection: React.FC<CompanyLogosSectionProps> = ({ appearance = 'default' }) => {
  const isDarkBar = appearance === 'darkBar';

  return (
    <div className="w-full max-w-7xl mx-auto mb-4 min-w-0">
      <div
        className={
          isDarkBar
            ? 'relative rounded-2xl border border-zinc-700/80 bg-zinc-900/95 py-3 px-4 sm:px-6 sm:py-4'
            : 'relative py-3 px-4 sm:py-4 sm:px-6 dark:rounded-[20px] dark:border dark:border-zinc-700 dark:bg-zinc-900 dark:shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset]'
        }
      >
        <div className="text-center">
          <div
            className={`flex flex-col items-center justify-center gap-y-3 gap-x-2 text-xs font-normal sm:flex-row sm:flex-wrap sm:text-sm ${
              isDarkBar ? 'text-zinc-300' : 'text-zinc-500 dark:text-zinc-300'
            }`}
          >
            <span className="max-w-[min(100%,280px)] leading-snug sm:max-w-none">
              Strategies Used By Marketers At
            </span>

            {/* Company logos — wrap on narrow screens */}
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 sm:ml-1 md:ml-3">
              {MARKETING_COMPANY_LOGOS.map((logo) => (
                <div key={logo.name} className="flex h-4 items-center justify-center">
                  {isDarkBar ? (
                    <img
                      src={logo.darkImageSrc}
                      alt={logo.alt}
                      className="h-full w-auto max-h-5 object-contain opacity-90"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = logo.lightImageSrc;
                      }}
                    />
                  ) : (
                    <>
                      <img
                        src={logo.lightImageSrc}
                        alt={logo.alt}
                        className="h-full w-auto object-contain dark:hidden"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = `<span class="text-zinc-500 dark:text-zinc-300 text-xs">${logo.name}</span>`;
                          }
                        }}
                      />
                      <img
                        src={logo.darkImageSrc}
                        alt={logo.alt}
                        className="hidden h-full w-auto object-contain dark:block"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = logo.lightImageSrc;
                          target.className = 'h-full w-auto object-contain';
                        }}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyLogosSection;
